import { Directive, ElementRef, Renderer2, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CountryDialCodeUtil } from '../model/country-dial-codes';

/**
 * ControlValueAccessor for telephone-kontrollen (bt-dynamic-form-control,
 * controlType "telephone").
 *
 * Bakgrunn: Det underliggende FormControl (bundet via [formControlName] på
 * DENNE inputen) skal alltid holde HELE telefonnummeret, inkludert
 * landsprefiks (f.eks. "+4799887766"). Men det synlige input-feltet skal
 * kun vise LOKALDELEN (f.eks. "99887766"), siden prefikset velges separat
 * i en egen nedtrekksboks ved siden av.
 *
 * NG-ZORRO sin automatiske rød-ramme/feilmelding-visning ("Feltet er
 * påkrevd") krever at selve nz-input-direktivet sitter på et element med
 * en ekte NgControl (formControlName/ngModel/formControl) - uten dette
 * blir aldri valideringsstatus pushet til nz-form-item. Ved å bruke denne
 * accessoren direkte på det synlige feltet får vi korrekt validering OG
 * riktig visning (kun lokalnummer) samtidig.
 *
 * - writeValue: kalles av Angular når FormControl-verdien endres
 *   (programmatisk, f.eks. ved innlogging eller når bruker endrer prefiks).
 *   Splitter opp verdien og viser kun lokaldelen i input-feltet, og lagrer
 *   gjeldende prefiks internt.
 * - onInput: kalles når bruker skriver i feltet. Kombinerer det bufrede
 *   prefikset med den nye lokalteksten og sender HELE verdien videre til
 *   Angular (som blir den nye FormControl-verdien).
 */
@Directive({
  selector: 'input[btTelephoneValueAccessor]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TelephoneValueAccessorDirective),
      multi: true,
    },
  ],
})
export class TelephoneValueAccessorDirective implements ControlValueAccessor {
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private prefix = '+47';

  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  writeValue(value: string): void {
    let split = CountryDialCodeUtil.splitTelephone(value, this.prefix);
    this.prefix = split.dialCode;
    this.renderer.setProperty(this.el.nativeElement, 'value', split.local);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const localValue = (event.target as HTMLInputElement).value;
    const combined = CountryDialCodeUtil.combineTelephone(this.prefix, localValue);
    this.prefix = CountryDialCodeUtil.splitTelephone(combined, this.prefix).dialCode;
    this.onChange(combined);
  }

  @HostListener('blur')
  onBlur(): void {
    this.writeValue(CountryDialCodeUtil.combineTelephone(this.prefix, this.el.nativeElement.value));
    this.onTouched();
  }
}
